import React, { useEffect } from 'react';
import { audienceRejected, AUD } from '../constants/tokenAudience';
import axios from 'axios';
import AdminNavbar from './AdminNavbar';
import AdminSidebar, { SIDEBAR_WIDTH } from './AdminSidebar';
import ReportIssue from './ReportIssue';
import AdminAIAssistant from './AdminAIAssistant';
import AdminPwaInstallPrompt from './AdminPwaInstallPrompt';

/**
 * @param {object} props
 * @param {boolean} [props.hideAssistant] suppress the fixed AI/bug launcher on
 *   this page.
 *
 * 🚨 AN OPT-OUT, NOT A LAYOUT SWITCH, AND IT COSTS SOMETHING. The launcher is
 * the only route to "report a bug" from an admin page, and AdminAIAssistant's
 * own note argues that control has to be reachable AT the moment somebody hits a
 * defect — a page without it is a page from which a defect cannot be reported
 * without navigating away first, which loses the route they were on.
 *
 * ⚠️ SO IT IS PASSED EXPLICITLY BY THE PAGE THAT WANTS IT, never inferred from a
 * pathname. A route-sniffing launcher would silently extend this to any URL that
 * happened to match, and nobody reading the Messages page would know the widget
 * was gone or why.
 */
export default function AdminLayout({ children, hideAssistant = false }) {
  useEffect(() => { document.title = 'DoorlyDesk | Admin Portal'; }, []);

  // Staff equivalent of the borrower 401 handler (AuthContext.js:110). A 24h
  // admin token expires daily (and on JWT_SECRET rotation); without this a
  // rejected token leaves failed calls + error states on screen instead of a
  // clean logout. Mounted only under AdminLayout (admin pages), and fires ONLY
  // for admin sessions — it clears the ADMIN token and never touches the
  // borrower doorly_token. The borrower interceptor, in turn, ignores 401s on
  // /admin paths, so the two never cross-clear each other.
  // ── 🚨 THE STAFF SESSION IS CHECKED BEFORE IT IS ADOPTED ──────────────────
  //
  // The borrower app gained this when the audience cutoff landed; the staff
  // portal never had it, and it has the same shape of problem plus one more.
  //
  // 🔑 NOTHING VALIDATED THE STAFF TOKEN CLIENT-SIDE AT ALL — not the audience,
  // not even the expiry. A staff member returning after 24 hours (the staff
  // token's whole life) booted into DoorlyDesk as though signed in, fired every
  // fetch the page makes, and collected a screenful of error states before the
  // 401 interceptor below replaced the page. The borrower side at least checked
  // `exp`; this checked nothing.
  //
  // ⚠️ SAME PARITY AS THE BORROWER CHECK, AND THE SAME LIMIT: the server decides.
  // This only lets the client reach the same conclusion a moment sooner, so a
  // dead session shows a login screen instead of a broken dashboard.
  //
  // ⚠️ RUNS BEFORE THE INTERCEPTOR EFFECT, and synchronously enough that the
  // redirect happens on the first paint rather than after a round trip.
  useEffect(() => {
    const raw = (() => { try { return localStorage.getItem('doorly_admin_token'); } catch { return null; } })();
    if (!raw) return;
    const drop = (why) => {
      console.warn(`[AdminAuth] staff session not adopted — ${why}`);
      try {
        localStorage.removeItem('doorly_admin_token');
        localStorage.removeItem('doorly_admin_user');
      } catch (_) { /* storage blocked — the 401 path still covers us */ }
      // ⚠️ THE SAME LOOP GUARD AS THE INTERCEPTOR. The pages that CREATE a
      // session must not be bounced by the check that reads one — and on
      // desk.godoorly.com they are served WITHOUT the /admin prefix, so the
      // suffix is what is matched.
      //
      // 🚨 THE FIRST VERSION DERIVED THESE FROM UNGUARDED_ADMIN_PATHS WITH
      // `p.replace('/admin', '')`, which turns the entry '/admin' into the EMPTY
      // STRING — and `pathname.includes('')` is true for every path. The guard
      // would have suppressed the redirect everywhere, leaving exactly the broken
      // screen it exists to prevent.
      const onSessionPage = ['/login', '/accept-invite', '/reset-password', '/no-access']
        .some((suffix) => window.location.pathname.endsWith(suffix));
      if (!onSessionPage) window.location.href = '/admin/login';
    };
    let payload;
    try {
      const parts = raw.split('.');
      if (parts.length !== 3) throw new Error('not 3 segments');
      payload = JSON.parse(atob(parts[1]));
    } catch (e) {
      drop(`malformed token (${e.message})`);
      return;
    }
    const nowMs = Date.now();
    if (!payload.exp || payload.exp * 1000 <= nowMs) { drop('expired'); return; }
    // 🚨 aud MUST BE 'staff' HERE. A BORROWER token in the staff portal is not an
    // old token, it is the wrong one, and the audience cutoff refuses an
    // audience-less staff token from 2026-11-06 — staff tokens live 24h, so by
    // then every live one carries the claim.
    if (audienceRejected(payload, AUD.STAFF, nowMs)) {
      drop(`audience no longer accepted (aud: ${payload.aud || 'none'})`);
    }
  }, []);

  useEffect(() => {
    const id = axios.interceptors.response.use(
      (r) => r,
      (error) => {
        if (error.response?.status === 401 && localStorage.getItem('doorly_admin_token')) {
          localStorage.removeItem('doorly_admin_token');
          localStorage.removeItem('doorly_admin_user');
          if (!window.location.pathname.includes('/admin/login')) {
            window.location.href = '/admin/login';
          }
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, []);
  return (
    <>
      <AdminNavbar />
      <AdminSidebar />
      <div
        className="admin-sidebar-offset admin-content"
        style={{
          // Matches the fixed navbar height (64px + iOS safe-area inset) so
          // content clears the header in the desk PWA standalone mode.
          paddingTop: 'calc(64px + env(safe-area-inset-top, 0px))',
          paddingLeft: `${SIDEBAR_WIDTH}px`,
          minHeight: '100vh',
          backgroundColor: '#f9fafb',
        }}
      >
        <AdminPwaInstallPrompt />
        {children}
        {/* ⚠️ ON EVERY ADMIN PAGE, DELIBERATELY. The moment someone hits a defect
            is the moment they will report it; a control living on one settings
            page is one nobody reaches while looking at the broken screen. It
            also means the captured route is the route they were actually on. */}
        <ReportIssue />
      </div>

      {/* 🔑 NOT RENDERED AT ALL, rather than hidden. A fixed element with
          visibility off still occupies its corner for hit-testing in some
          browsers, and "the button is behind something invisible" is a worse
          bug than the one being fixed. */}
      {!hideAssistant && <AdminAIAssistant />}
      <style>{`
        /* ── Opt-in content gutters ─────────────────────────────────────
           Sections tagged .admin-content-pad get 28px side gutters on
           desktop, shrinking to 12px on mobile so a 375px phone keeps more
           usable width. Vertical padding stays inline on each section. */
        .admin-content-pad { padding-left: 28px; padding-right: 28px; }

        /* ── 🔑 THE SHARED PAGE HEADER ────────────────────────────────────
           Jesse: "on the Partner Leads page, the header and copy is wrapping
           and in different placement than the Partners and Accounts pages. Fix
           to be consistent."

           They differed because each page solved it alone: Partners grew a
           local media block, Accounts relied on flex-wrap, and Leads had
           neither — so one stacked, one wrapped and one squeezed a 95-character
           sentence into the ~150px the buttons did not want. Three pages in one
           section that lay out their header three ways is three pages a rep has
           to learn separately.

           ⚠️ SO THE RULE LIVES HERE, ONCE. A fourth page gets it by adding two
           class names rather than by copying a media query that will drift. */
        .admin-page-body { padding: 28px 32px; }
        .admin-page-head {
          display: flex; justify-content: space-between;
          align-items: flex-start; gap: 16px; margin-bottom: 20px;
        }
        /* ⚠️ THE ACTION GROUP NEVER SHRINKS ON DESKTOP — a wrapped button label
           is what made these headers look different in the first place. */
        .admin-page-head > :last-child { flex-shrink: 0; }

        /* Phase-2 mobile utility hooks — defined here for reuse; not all
           applied yet. Base (desktop) state lives outside the media query;
           the @media block below flips them on narrow screens. */
        .admin-hide-mobile { display: block; }
        .admin-show-mobile { display: none; }

        /* Element-specific mobile hides (DoorlyDesk mobile cleanup). Base
           (desktop) display preserved; the @media block hides them on phones. */
        .admin-desk-badge { display: inline-flex; }
        .admin-partner-filter { display: block; }
        .admin-borrowers-cog-section { display: flex; }

        /* Borrower-profile tab bar — scrollbar hidden always (it scrolls
           horizontally via swipe/shift-wheel); pinned only on mobile below. */
        .admin-tabs-bar { -ms-overflow-style: none; scrollbar-width: none; }
        .admin-tabs-bar::-webkit-scrollbar { display: none; }

        /* ── Mobile breakpoint: <= 768px ───────────────────────────────── */
        @media (max-width: 768px) {
          .admin-content-pad { padding-left: 12px !important; padding-right: 12px !important; }
          /* 🔑 The header logo keeps its production 32px on a phone; 38px is desktop only (Hannah 2026-11-03). */
          .desk-logo-img { height: 32px !important; }

          /* ── the shared header, stacked ────────────────────────────────
             🔑 THE SUBTITLE GETS THE FULL WIDTH, which is two lines rather than
             six. The copy is not cut: it is the one sentence saying what the
             page is for, and a reader on a phone needs it as much as one on a
             monitor.

             ⚠️ 32px EACH SIDE IS 64px OF A 390px SCREEN — a sixth of the width.
             20px/16px matches what this block already gives tables. */
          .admin-page-body { padding: 20px 16px !important; }
          .admin-page-head { flex-direction: column; gap: 14px; }
          /* 🔑 THE ACTION ROW SPANS THE WIDTH once it sits below the copy rather
             than beside it — left-hugging buttons read as unaligned with
             everything under them. */
          .admin-page-head > :last-child { width: 100%; }
          /* ⚠️ AND THE BUTTONS INSIDE IT SHARE THAT WIDTH rather than keeping
             their desktop hug, so two actions do not leave a ragged right edge. */
          .admin-page-head > :last-child > button { flex: 1 1 auto; }
          .admin-hide-mobile { display: none !important; }
          .admin-show-mobile { display: block !important; }
          .admin-stack-mobile { flex-direction: column !important; }
          .admin-full-mobile { width: 100% !important; max-width: 100% !important; }
          .admin-desk-badge { display: none !important; }
          .admin-partner-filter { display: none !important; }
          .admin-borrowers-cog-section { display: none !important; }

          /* Borrower profile (mobile): the borrower header scrolls away (so it
             can't cover the next-action card), and the tab bar pins just below
             the navbar instead — horizontal scroll only. */
          .admin-bp-sticky-header { position: static !important; top: auto !important; }
          .admin-tabs-bar {
            position: sticky !important;
            top: calc(64px + env(safe-area-inset-top, 0px));
            z-index: 90;
            background: #ffffff;
          }

          /* Clear the desktop sidebar reserve; hamburger drawer replaces it */
          .admin-content { padding-left: 0 !important; }

          /* Tables get horizontal scroll so wide loan-tape views stay legible */
          .admin-content table {
            display: block;
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
          }
          .admin-content table thead,
          .admin-content table tbody,
          .admin-content table tr { display: revert; }
          .admin-content table th,
          .admin-content table td {
            padding: 8px 10px !important;
            font-size: 11px !important;
            white-space: nowrap;
          }

          /* Opt-in mobile utilities — pages can use these directly */
          .admin-mobile-grid { grid-template-columns: repeat(2, 1fr) !important; gap: 8px !important; }
          .admin-mobile-stack { grid-template-columns: 1fr !important; }
          .admin-mobile-wrap { flex-wrap: wrap !important; gap: 8px !important; }
          .admin-mobile-pad { padding: 14px 12px !important; }
          /* Pages tag any 2-col-or-wider grid with this class to stack on
             mobile. Reliable substitute for the previous substring matcher,
             which silently missed any inline whitespace drift. */
          .admin-two-col-grid { grid-template-columns: 1fr !important; }

          /* ── 🚨 overflow-x: hidden HERE KILLED EVERY STICKY BAR ON MOBILE ──
             Jesse: "The Borrower Pipeline page has some formatting issues —
             there is a grey section between the top nav bar and the search
             borrowers and filter bar section." Fixed once, and still wonky,
             because the cause was never in that page.

             🔑 PER CSS OVERFLOW, SETTING ONE AXIS TO ANYTHING BUT 'visible'
             FORCES THE OTHER TO 'auto'. So 'overflow-x: hidden' quietly made
             this wrapper a scroll container in BOTH axes — and 'position:
             sticky' resolves against its nearest scrollport, which is now this
             element rather than the viewport. This element never scrolls
             internally (the document does), so the bar could never pin: it
             scrolled away like any other block, leaving page background between
             it and the fixed navbar. That grey band IS the dead sticky.

             ⚠️ IT WAS NOT ONE PAGE. Twenty-four sticky elements live under this
             wrapper — the borrower filter bar, the profile tab bar, the
             underwriting-guide rail — and all of them were inert on a phone and
             correct on a desktop, which is why it read as a per-page layout bug
             every time somebody looked at it.

             🔑 'clip' IS THE ONE VALUE THAT CLIPS WITHOUT SCROLLING, and it is
             explicitly exempt from the rule above: 'overflow-x: clip' leaves
             'overflow-y: visible', so no scrollport is created and sticky
             resolves against the viewport again. The runaway-scroll guard this
             line exists for is unchanged.

             ⚠️ WITH A FALLBACK, NOT A BET. Safari gained 'overflow: clip' in
             16.0; anything older keeps the old behaviour rather than losing the
             clip, because horizontal runaway is the worse failure of the two. */
          .admin-content { overflow-x: clip; }
          @supports not (overflow: clip) {
            .admin-content { overflow-x: hidden; }
          }

          /* Modals: cap width to viewport with safe edges */
          .admin-content [role="dialog"],
          .admin-content .modal-card {
            max-width: calc(100vw - 32px) !important;
          }

          /* Headings breathe less on narrow screens */
          .admin-content h1 { font-size: 22px !important; }
          .admin-content h2 { font-size: 18px !important; }
          .admin-content h3 { font-size: 15px !important; }
        }

        /* ── Small-phone breakpoint: <= 480px ─────────────────────────── */
        @media (max-width: 480px) {
          .admin-mobile-grid { grid-template-columns: 1fr !important; }
          /* Tab bars benefit from touch-scroll momentum on narrow screens */
          .admin-content [style*="overflow-x: auto"] {
            -webkit-overflow-scrolling: touch;
          }
        }
      `}</style>
    </>
  );
}
